import Compressor from '@uppy/compressor';
import type { Body, Meta, Uppy } from '@uppy/core';

import type { TrackingFn } from '@change-corgi/core-react/tracking';

import {
	getMaxImageFileSize,
	getMaxRawImageFileSizeForCompression,
	RestrictionUploadError,
} from '#src/app/shared/utils/uppyUploader';

const COMPRESSOR_MAX_DIMENSION_PX = 2000;
const COMPRESSOR_QUALITY = 0.6;

type CompressionMeta = { originalSize?: number; originalType?: string };

/**
 * `@uppy/compressor` mutates a file's top-level `size`/`type` in place on success, so the only
 * way to know what it looked like before compression is to capture it here, before Compressor's
 * own preprocessor runs (registration order) — stored on file.meta so it survives into both the
 * postprocess phase (success) and the rejection thrown below (still-too-large failure). `setFileMeta`
 * merges into existing meta (confirmed against @uppy/core@5.2.0 source), so this survives AwsS3's
 * own later `setFileMeta` call (storing `filePublicUrl`) without either clobbering the other.
 */
function captureOriginalMeta(uppy: Uppy<Meta, Body>): (fileIDs: string[]) => void {
	return (fileIDs) => {
		fileIDs.forEach((fileID) => {
			const file = uppy.getFile(fileID);
			uppy.setFileMeta(fileID, { originalSize: file.size ?? 0, originalType: file.type });
		});
	};
}

function getCompressionMeta(uppy: Uppy<Meta, Body>, fileID: string): CompressionMeta {
	// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
	return uppy.getFile(fileID).meta as CompressionMeta;
}

function trackCompressionResult(
	original: { size: number; type: string },
	compressed: { size: number; type: string },
	track: TrackingFn,
): void {
	void track('rich_media_image_compress', {
		original_file_size_bytes: original.size,
		compressed_file_size_bytes: compressed.size,
		original_file_type: original.type,
		compressed_file_type: compressed.type,
		// Best-effort signal: the compressor plugin fails open (falls back to the original file)
		// without surfacing a per-file error, so an unchanged size/type is our proxy for "didn't compress".
		compression_failed: compressed.size === original.size && compressed.type === original.type,
	});
}

/**
 * Runs after the compressor's own preprocessor (registration order), so it sees the compressed
 * size — rejecting a file here means it's still too large even after compression, rather than
 * rejecting the (often much larger) original upfront. Tracks before throwing: a thrown
 * preprocessor error aborts Uppy's upload() step loop before it ever reaches the postprocess
 * phase, so this is the only place that can report on a file that gets rejected here — the
 * postprocessor below never runs for it. Thrown as a RestrictionUploadError so it routes through
 * Uppy's own centralized 'restriction-failed' handling (see useUploader.ts) instead of a second,
 * ad-hoc error path — handleRestrictionFailed reads its typed `reason` directly, so this message
 * is free to read naturally rather than needing to match getUploadErrorReason's string patterns.
 */
function rejectIfStillTooLarge(
	uppy: Uppy<Meta, Body>,
	{ maxCompressedFileSize, track }: { maxCompressedFileSize: number; track: TrackingFn },
): (fileIDs: string[]) => void {
	return (fileIDs) => {
		fileIDs.forEach((fileID) => {
			const file = uppy.getFile(fileID);
			const meta = getCompressionMeta(uppy, fileID);
			const size = file.size ?? 0;
			if (size > maxCompressedFileSize) {
				trackCompressionResult(
					{ size: meta.originalSize ?? size, type: meta.originalType ?? file.type },
					{ size, type: file.type },
					track,
				);
				throw new RestrictionUploadError(
					'IMAGE_TOO_LARGE',
					'File exceeds the maximum allowed size, even after compression',
				);
			}
		});
	};
}

/**
 * Runs after the actual network upload completes. Only reached when the file made it through
 * preprocessing (i.e. wasn't rejected by rejectIfStillTooLarge above) — covers both a successful
 * upload and a failed one, since Uppy's uploader plugins settle per-file rather than throwing.
 */
function trackOnComplete(uppy: Uppy<Meta, Body>, track: TrackingFn): (fileIDs: string[]) => void {
	return (fileIDs) => {
		fileIDs.forEach((fileID) => {
			const file = uppy.getFile(fileID);
			const meta = getCompressionMeta(uppy, fileID);
			const size = file.size ?? 0;
			trackCompressionResult(
				{ size: meta.originalSize ?? size, type: meta.originalType ?? file.type },
				{ size, type: file.type },
				track,
			);
		});
	};
}

type RegisterCompressorOptions = {
	/** Post-compression size ceiling; defaults to getMaxImageFileSize(). */
	maxCompressedFileSize?: number;
	/** Reports the compression outcome once it's known — the only place that ever knows it. */
	track: TrackingFn;
};

/**
 * Registers image compression as an Uppy preprocessor, so it runs automatically during upload()
 * regardless of how the file was added. Also widens the instance's raw (pre-compression) file-size
 * restriction internally, since compression now happens mid-pipeline instead of before addFile()
 * — otherwise a large-but-compressible original would be rejected before ever reaching the
 * compressor. Also owns reporting the compression outcome via `track`, scoped per-upload-call by
 * Uppy's own addPreProcessor/addPostProcessor (not a shared event listener), so concurrent
 * uploads can't cross-report each other's results. Callers only need to opt into compression;
 * they don't need to know the raw ceiling or tracking mechanics exist.
 */
export function registerCompressor(
	uppy: Uppy<Meta, Body>,
	{ maxCompressedFileSize = getMaxImageFileSize(), track }: RegisterCompressorOptions,
): void {
	uppy.setOptions({ restrictions: { maxFileSize: getMaxRawImageFileSizeForCompression() } });
	if (uppy.getPlugin('Compressor')) return;
	uppy.addPreProcessor(captureOriginalMeta(uppy));
	uppy.use(Compressor, {
		quality: COMPRESSOR_QUALITY,
		maxWidth: COMPRESSOR_MAX_DIMENSION_PX,
		maxHeight: COMPRESSOR_MAX_DIMENSION_PX,
	});
	uppy.addPreProcessor(rejectIfStillTooLarge(uppy, { maxCompressedFileSize, track }));
	uppy.addPostProcessor(trackOnComplete(uppy, track));
}
